import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card, CardHeader, CardBody, Row, Col,
  Nav, NavItem, NavLink,
  TabContent, TabPane,
  Button
} from "reactstrap";
import classnames from "classnames";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import { AlumnoService } from "service/AlumnoService";
import TabInfoAlumno from "components/Alumno/TabInfoAlumno";
import TabMatriculas from "components/Alumno/TabMatriculas";

const cicloService = new CicloService();
const salonService = new SalonService();
const alumnoService = new AlumnoService();

function AlumnoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const isNew = id === "nuevo";

  const [activeTab, setActiveTab] = useState("info");
  const [alumnoId, setAlumnoId] = useState(isNew ? null : id);
  const [alumno, setAlumno] = useState(null);
  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [listModulos, setListModulos] = useState([]);

  const notify = (type, message) => {
    notificationAlertRef.current?.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      const [resCiclos, resSalones, resModulos] = await Promise.all([
        cicloService.getCiclos(),
        salonService.getSalones(),
        alumnoService.getModulos(),
      ]);
      setListCiclos(resCiclos?.data || []);
      setListSalones(resSalones?.data || []);
      setListModulos(resModulos?.data || []);
    } catch (error) {
      console.error("Error cargando datos maestros:", error);
    }
  };

  // Callback desde TabInfoAlumno cuando el alumno es creado, encontrado o cargado
  const handleAlumnoReady = (alumnoData, newId) => {
    const resolvedId = newId || alumnoData?._id;
    setAlumno(alumnoData);
    if (resolvedId && resolvedId !== alumnoId) {
      setAlumnoId(resolvedId);
      navigate(`/admin/alumno/${resolvedId}`, { replace: true });
    }
    // Al registrar un alumno nuevo, ir directo a la pestaña de matrículas
    if (isNew && resolvedId) {
      setActiveTab("matriculas");
    }
  };

  const pageTitle = alumno
    ? `${alumno.apellidopaterno || ""} ${alumno.apellidomaterno || ""}, ${alumno.nombres || ""}`
    : (isNew ? "Nuevo Alumno / Matrícula" : "...");

  const canShowMatriculas = !!alumnoId;

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="auto">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      onClick={() => navigate("/admin/alumno")}
                      title="Volver a la lista de alumnos"
                    >
                      <i className="tim-icons icon-minimal-left" />
                    </Button>
                  </Col>
                  <Col>
                    <h4 className="card-title mb-0">{pageTitle}</h4>
                    <p className="card-category mb-0">Administración de Matrículas</p>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Navegación por tabs */}
                <Nav tabs className="mb-0">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "info" })}
                      onClick={() => setActiveTab("info")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="tim-icons icon-single-02 mr-2" />
                      Información del Alumno
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({
                        active: activeTab === "matriculas",
                        disabled: !canShowMatriculas
                      })}
                      onClick={() => canShowMatriculas && setActiveTab("matriculas")}
                      style={{ cursor: canShowMatriculas ? "pointer" : "not-allowed" }}
                      title={!canShowMatriculas ? "Primero registra o carga un alumno" : undefined}
                    >
                      <i className="tim-icons icon-notes mr-2" />
                      Matrículas
                      {!canShowMatriculas && (
                        <small className="ml-1 text-muted">(registrar alumno primero)</small>
                      )}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-4">
                  <TabPane tabId="info">
                    <TabInfoAlumno
                      alumnoId={alumnoId}
                      onAlumnoReady={handleAlumnoReady}
                      notify={notify}
                    />
                  </TabPane>

                  <TabPane tabId="matriculas">
                    {canShowMatriculas && (
                      <TabMatriculas
                        alumnoId={alumnoId}
                        alumno={alumno || {}}
                        listCiclos={listCiclos}
                        listSalones={listSalones}
                        listModulos={listModulos}
                        notify={notify}
                      />
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default AlumnoDetalle;
